import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import Graphic from '@arcgis/core/Graphic';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import { DrawingPlaceRequest, DrawingRevisionDetail } from '../../../../../../../../shared/models/drawing-sheet';
import { ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { ProjectMapSourceService } from '../../../../../../../../shared/services/project-map-source.service';
import { MapReferenceLayerService } from '../../../../../../../../shared/services/map-reference-layer.service';
import { clampZoomToPublishedMax } from '../../../../../../../../shared/services/project-map-manifest.service';
import { AuthService } from '../../../../../../../../shared/services/auth.service';
import { EsriMapComponent } from '../../../../../../../../shared/components/esri-map/esri-map.component';
import { DEFAULT_MAP_TOOLBAR_STATE, MapToolbarState } from '../../../../../../../../shared/types/map.types';
import { loadBasemapPreference, resolveBasemap, saveBasemapPreference } from '../../../../../../../../shared/helpers/map-layer-view-state';
import { sheetImageReady, sheetPointFromClick } from '../../../../../../../../shared/helpers/sheet-point';
import { FT, ProjectAlignment, projectAlignments, stationingOf, stationName, stationRange } from '../../../../../../../../shared/helpers/alignment-stationing';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { PLACE_SHEET_ZOOMS } from '../drawing-markers';
import { ScrollSheetZoom, sharpRegion } from '../sheet-zoom-pan';
import { PDF_PAGE_RANGES, PdfRenderService } from '../../../../../../../../shared/services/pdf-render.service';
import type { PDFDocumentLoadingTask, RenderTask } from 'pdfjs-dist';
import { fitSimilarity, PlacePair, SimilarityFit } from '../drawing-place-fit';
import { DrawingsAnalytics, errorProps } from '../drawings-analytics';
import { geometryVertices, nearestVertex, ScreenVertex, SNAP_RADIUS_PX } from '../drawing-snap';
import {
  alignmentNameProblem, chooseSide, entrySide, readStationEntry, SideChoice, sideHelper, snapToAlignment, stationEntryReady, stationGround,
  stationInputText, stationIntervals, stationLabelStations, StationPair, stationPlaceBody, stationRangeBounds, stationRangeText, stationReadout,
  stationTicks, typeOffset,
} from '../drawing-place-stations';

export interface DrawingPlaceDialogData {
  projectId: string;
  revision: DrawingRevisionDetail;
  /** The flagged map layer this placement replaces (follow current, R21), when the viewer found exactly one */
  supersedesLayerId?: string | null;
  /** Every flagged layer of this sheet, offered when the API cannot tell which one to replace */
  flaggedLayers?: { id: string; name: string }[];
  /** The sheet's stations (feet), when known: By stations fits the map to them on the alignment */
  stations?: { start: number | null; end: number | null } | null;
}

export interface DrawingPlaceDialogResult {
  sourceUuid: string;
  versionUuid: string;
  /** The Drawings map group to publish into (W22) */
  groupId: string | null;
  groupName: string;
}

type Phase = 'points' | 'placing' | 'failed';

/** Spec §12: status "Approximate" until the check error is within the project tolerance (1.0 m default). */
const TOLERANCE_M = 1;
const POLL_MS = 3000;
/** The converter's deadline is minutes; past this the dialog stops waiting and says where to look. */
const POLL_LIMIT = 200;
const DEFAULT_CENTER = { latitude: 39.5, longitude: -98.35 };
const CONTROL_COLOR = '#E02020';
const CHECK_COLOR = '#1036D6';
/** Hover snapping is recomputed at most this often while the pointer moves */
const SNAP_HOVER_MS = 60;
/** The sharp redraw waits for a zoom or scroll to settle */
const SHARP_DEBOUNCE_MS = 200;
/** By stations: the alignment's line and its station ticks / labels */
const ALIGNMENT_COLOR = '#1036D6';
const TICK_HALF_PX = 6;
/** The hover readout shows while the pointer is this close to the line (pixels) */
const READOUT_PX = 60;

/**
 * Place on map (spec §12 slice 4). The page image on the left takes a region (drag) and sheet points (click); the
 * project map on the right takes the matching map point for the last unpaired sheet point. Fit previews the similarity
 * the converter will run. Place stages the placement and waits for the import to be ready for review; the caller then
 * opens the map layer review to publish it.
 */
@Component({
  selector: 'app-drawing-place-dialog',
  templateUrl: './drawing-place-dialog.component.html',
  styleUrls: ['./drawing-place-dialog.component.scss'],
  providers: [MapReferenceLayerService],
  standalone: false,
})
export class DrawingPlaceDialogComponent implements OnDestroy {
  readonly data = inject<DrawingPlaceDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<DrawingPlaceDialogComponent, DrawingPlaceDialogResult>>(MatDialogRef);
  private api = inject(ProjectDrawingsService);
  private sources = inject(ProjectMapSourceService);
  private referenceLayers = inject(MapReferenceLayerService);
  private auth = inject(AuthService);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  /** W48: PostHog events; how this placement was made */
  private track = inject(DrawingsAnalytics);
  private trackedMethod: 'points' | 'stations' | 'embedded' = 'points';

  phase: Phase = 'points';
  mode: 'points' | 'stations' | 'region' = 'points';
  /** How the sheet points are tied to the ground: map clicks, or stations along the project alignment (§17 rule 5) */
  method: 'points' | 'stations' = 'points';
  pairs: PlacePair[] = [];
  /** By stations: the project's alignments, the one the stations are on, and the points placed so far */
  alignments: ProjectAlignment[] = [];
  alignmentIndex: number | null = null;
  stationPairs: StationPair[] = [];
  stationText = '';
  /** A number once typed (type=number) */
  offsetText = '0';
  /** Left | On the line | Right (none: an offset typed while On the line, which then needs one) */
  sideChoice: SideChoice = 'on';
  /** By stations: the map's readout under the pointer near the line, "STA 1006+42 · 12 ft R" */
  hoverReadout: { text: string; x: number; y: number } | null = null;
  /** A sheet point waiting for its map point */
  pendingSheet: { x: number; y: number } | null = null;
  /** The next pair is a check point */
  nextIsCheck = false;
  region: { x0: number; y0: number; x1: number; y1: number } | null = null;
  pageLoaded = false;
  zoom = 1;
  readonly minZoom = PLACE_SHEET_ZOOMS[0];
  readonly maxZoom = PLACE_SHEET_ZOOMS[PLACE_SHEET_ZOOMS.length - 1];
  /** The sheet pane's pinch zoom and drag pan (sheet-zoom-pan.ts); Draw area keeps the drag for the area */
  readonly sheetZoom = new ScrollSheetZoom({
    ladder: PLACE_SHEET_ZOOMS,
    zoom: () => this.zoom,
    setZoom: z => {
      this.zoom = z;
      this.cdr.detectChanges();
      this.scheduleSharp();
    },
    page: () => this.pageImage?.nativeElement ?? null,
    canPan: () => this.mode !== 'region',
  });
  fit: SimilarityFit | null = null;
  error = '';
  status = '';
  placing = false;
  /** Set when the API needs the user to pick which flagged layer this replaces */
  chooseLayer = false;
  supersedesLayerId: string | null;
  center: number[];
  basemapId: string | null = null;
  organizationBasemaps: { id?: string }[] = [];
  layersToolbar: MapToolbarState = { ...DEFAULT_MAP_TOOLBAR_STATE, overlayLoading: true };
  readonly columns = ['index', 'kind', 'sheet', 'map', 'remove'];
  readonly stationColumns = ['index', 'kind', 'sheet', 'station', 'remove'];
  readonly tolerance = TOLERANCE_M;
  readonly sideOptions: { value: SideChoice; label: string }[] = [{ value: 'L', label: 'Left' }, { value: 'on', label: 'On the line' }, { value: 'R', label: 'Right' }];
  @ViewChild('pageImage') private pageImage?: ElementRef<HTMLImageElement>;
  @ViewChild('sheetScroll') set sheetScroll(ref: ElementRef<HTMLElement> | undefined) {
    this.scrollEl = ref?.nativeElement ?? null;
    this.sheetZoom.attach(this.scrollEl);
  }
  /** Zoomed in: the part of the page in view, drawn sharp from the PDF over the preview image (which stretches) */
  @ViewChild('sharpHost') private sharpHost?: ElementRef<HTMLElement>;
  private readonly pdf = inject(PdfRenderService);
  private scrollEl: HTMLElement | null = null;
  private pdfDoc: PDFDocumentLoadingTask | null = null;
  private sharpTask: RenderTask | null = null;
  private sharpTimer: ReturnType<typeof setTimeout> | null = null;
  private sharpSeq = 0;
  @ViewChild(EsriMapComponent) private esriMap?: EsriMapComponent;
  private mapView: __esri.MapView | null = null;
  private pointsLayer: GraphicsLayer | null = null;
  /** By stations: the chosen alignment with its station ticks and labels */
  private alignmentLayer: GraphicsLayer | null = null;
  /** The snap marker: where a click would land, while the pointer is near a vertex */
  private snapLayer: GraphicsLayer | null = null;
  private snapHoverAt = 0;
  private snapSequence = 0;
  /** The last pointer position while throttled: it is still looked at once the window passes (trailing edge) */
  private snapHoverTimer: ReturnType<typeof setTimeout> | null = null;
  /** One hover query at a time; the latest position waiting for it to finish */
  private snapHoverBusy = false;
  private snapHoverNext: { x: number; y: number } | null = null;
  /** The last pair's map point snapped to a design-layer vertex (the hint says so) */
  snapped = false;
  private handles: { remove(): void }[] = [];
  private dragFrom: { x: number; y: number } | null = null;
  private polls = 0;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor() {
    this.supersedesLayerId = this.data.supersedesLayerId ?? null;
    let seed = DEFAULT_CENTER;
    this.store.select(projectDetails).pipe(take(1)).subscribe(project => {
      const p = project as { id?: string; latitude?: unknown; longitude?: unknown; base_maps?: { id?: string }[]; alignment_geometry?: unknown } | null;
      if (p?.id !== this.data.projectId) {
        return;
      }
      this.alignments = projectAlignments(p.alignment_geometry);
      this.alignmentIndex = this.alignments.length === 1 ? 0 : null;
      const latitude = Number(p.latitude);
      const longitude = Number(p.longitude);
      if (Number.isFinite(latitude) && Number.isFinite(longitude) && (latitude || longitude)) {
        seed = { latitude, longitude };
      }
      if (Array.isArray(p.base_maps)) {
        this.organizationBasemaps = p.base_maps;
      }
    });
    this.center = [seed.longitude, seed.latitude];
    this.basemapId = loadBasemapPreference(this.auth.getCurrentUser()?.id, this.data.projectId) || 'default';
  }

  get title(): string {
    const r = this.data.revision;
    return `${r.sheet?.number ?? 'Sheet'} Rev ${r.label ?? ''}`.trim();
  }

  get controlCount(): number {
    return this.activePairs.filter(p => !p.check).length;
  }

  /** The sheet points of the current method, numbered on the sheet and the map */
  get activePairs(): { sheet: { x: number; y: number }; check?: boolean }[] {
    return this.method === 'stations' ? this.stationPairs : this.pairs;
  }

  /** The chosen alignment for By stations */
  get alignment(): ProjectAlignment | null {
    return this.alignmentIndex === null ? null : this.alignments[this.alignmentIndex] ?? null;
  }

  /** R44c: the chosen alignment's stations are chainage, not the plan's */
  get alignmentChainage(): boolean {
    const a = this.alignment;
    return !!a && stationingOf(a.alignment) === 'chainage';
  }

  /** Its station range, "973+66 to 1105+93" */
  get alignmentRangeText(): string {
    return this.alignment ? stationRangeText(this.alignment) : '';
  }

  /** The chosen alignment can't be named to the API (unnamed or a shared name among several) */
  get alignmentProblem(): string | null {
    return alignmentNameProblem(this.alignments, this.alignment);
  }

  get canPlace(): boolean {
    if (this.phase !== 'points' || this.placing || this.pendingSheet || (this.chooseLayer && !this.supersedesLayerId)) {
      return false;
    }
    // Two stations fix a placement (the API derives the third control point); map points need three
    if (this.method === 'stations') {
      return !!this.alignment && !this.alignmentProblem && this.controlCount >= 2 && this.stationPairs.every(p => !!p.map);
    }
    return this.controlCount >= 3;
  }

  /** W34: two stations always fit exactly, so only a check point says whether the placement is right */
  get needsCheckPoint(): boolean {
    return this.method === 'stations' && this.controlCount >= 2 && !this.stationPairs.some(p => p.check);
  }

  get canUseEmbedded(): boolean {
    return this.phase === 'points' && !this.placing && (!this.chooseLayer || !!this.supersedesLayerId);
  }

  /** "Approximate" until a check point is within tolerance; the converter decides for real. */
  get fitApproximate(): boolean {
    return !this.fit || this.fit.check_error_m === null || this.fit.check_error_m > TOLERANCE_M;
  }

  get hint(): string {
    if (this.mode === 'region') {
      return 'Drag on the sheet to choose the area to place.';
    }
    if (this.method === 'stations') {
      if (!this.alignment) {
        return 'Choose the alignment the sheet\'s stations are on.';
      }
      return this.pendingSheet
        ? 'Now click that station on the map (or type it).'
        : 'Click a station mark on the sheet, then click that station on the map or type it below.';
    }
    if (this.pendingSheet) {
      return `Now click the same spot on the map for point ${this.pairs.length + 1}. Clicks snap to design-layer vertices; hold Alt to click freely.`;
    }
    if (this.snapped && this.pairs.length) {
      return `Point ${this.pairs.length} snapped to a design-layer vertex. Click a spot on the sheet for the next one.`;
    }
    return 'Click a spot on the sheet, then the same spot on the map.';
  }

  /** Pair number shown on both sides; check points are numbered in the same sequence. */
  label(index: number): string {
    return `${index + 1}`;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sheetZoom.detach();
    this.clearSharp();
    void this.pdfDoc?.destroy();
    this.pdfDoc = null;
    this.clearSnap();
    this.handles.forEach(h => h.remove());
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  }

  close(): void {
    this.clearSnap();
    this.dialogRef.close();
  }

  setMode(mode: 'points' | 'stations' | 'region'): void {
    this.mode = mode;
    this.dragFrom = null;
    if (mode !== 'region' && mode !== this.method) {
      // Each method keeps its own points; Place sends the shown method's
      this.method = mode;
      this.pendingSheet = null;
      this.resetNextPoint();
      this.error = '';
      this.clearSnap();
      this.hoverReadout = null;
      this.refit();
      this.drawMapPoints();
      this.drawAlignment();
      if (mode === 'stations') {
        this.fitAlignment();
      }
    }
  }

  chooseAlignment(index: number | null): void {
    this.alignmentIndex = index;
    this.error = '';
    // The same stations on another alignment are other ground points
    this.stationPairs = this.stationPairs.map(p => ({ ...p, map: this.stationPoint(p.station, p.offset_ft, p.side) }));
    const off = this.stationPairs.filter(p => !p.map);
    if (off.length && this.alignment) {
      this.error = `${off.length === 1 ? 'A station is' : 'Some stations are'} not on ${this.alignment.name || 'this alignment'} (${this.alignmentRangeText}).`;
    }
    this.refit();
    this.drawMapPoints();
    this.drawAlignment();
    this.fitAlignment();
  }

  /** Left | On the line | Right; On the line sets the offset to 0 */
  pickSide(side: SideChoice): void {
    const next = chooseSide({ side: this.sideChoice, offset: this.offsetText }, side);
    this.sideChoice = next.side;
    this.offsetText = next.offset;
    this.drawMapPoints();
  }

  /** An offset typed while On the line leaves no side picked (it then needs Left or Right) */
  onOffsetInput(value: string | number | null): void {
    const next = typeOffset({ side: this.sideChoice, offset: this.offsetText }, value === null || value === undefined ? '' : String(value));
    this.sideChoice = next.side;
    this.offsetText = next.offset;
    this.drawMapPoints();
  }

  /** Typing the station moves its marker on the map */
  onStationInput(value: string): void {
    this.stationText = value ?? '';
    this.drawMapPoints();
  }

  /** "Pick Left or Right" (needed), "On the line?" (a hint), or nothing */
  get sideHelp(): { text: string | null; needsSide: boolean } {
    return sideHelper({ side: this.sideChoice, offset: this.offsetText });
  }

  /** Add station: a sheet point waiting, an alignment, a valid station and a side that goes with the offset */
  get canAddStation(): boolean {
    return this.phase === 'points' && !!this.pendingSheet && !!this.alignment && stationEntryReady(this.stationText, { side: this.sideChoice, offset: this.offsetText });
  }

  /** Adds the waiting sheet point at the station typed for it */
  addStation(): void {
    const alignment = this.alignment;
    if (this.phase !== 'points' || !this.pendingSheet || !alignment) {
      return;
    }
    if (!this.canAddStation) {
      return;
    }
    const entry = readStationEntry(alignment, { station: this.stationText, offset: this.offsetText, side: entrySide(this.sideChoice) });
    if (entry.ok === false) {
      this.error = entry.error;
      return;
    }
    const { station, offset_ft, side, map } = entry;
    this.error = '';
    this.stationPairs = [...this.stationPairs, { sheet: this.pendingSheet, station, offset_ft, side, check: this.nextIsCheck, map }];
    this.pendingSheet = null;
    this.resetNextPoint();
    this.refit();
    this.drawMapPoints();
    // The station may be anywhere along the line: bring it into view
    this.mapView?.goTo({ center: [map.lon, map.lat] }, { duration: 300 }).catch(() => undefined);
  }

  /** "STA 1018+50, 12 ft L" */
  stationLabel(p: StationPair): string {
    return `STA ${stationName(p.station)}${p.offset_ft > 0 ? `, ${Math.round(p.offset_ft * 10) / 10} ft ${p.side}` : ''}`;
  }

  clearRegion(): void {
    this.region = null;
  }

  zoomBy(step: 1 | -1): void {
    this.sheetZoom.zoomStep(step);
  }

  onPageLoad(): void {
    this.pageLoaded = true;
    this.refit();
    this.scheduleSharp();
  }

  /** A zoom or scroll settled: draw the part in view sharp (debounced; a newer one cancels an older render) */
  scheduleSharp(): void {
    if (this.sharpTimer) {
      clearTimeout(this.sharpTimer);
    }
    this.sharpTimer = setTimeout(() => {
      this.sharpTimer = null;
      void this.renderSharp();
    }, SHARP_DEBOUNCE_MS);
  }

  /**
   * The page region in the pane, from the sheet's PDF page at the zoom × the screen's pixel ratio (sharpRegion keeps the
   * canvas inside the browser's limits), on a canvas laid over that region. At 1× the preview is sharp enough: none.
   * Points, marks and snapping stay on the image and its % box; the canvas takes no pointer events.
   */
  private async renderSharp(): Promise<void> {
    const img = this.pageImage?.nativeElement;
    const pane = this.scrollEl;
    const host = this.sharpHost?.nativeElement;
    const file = this.data.revision.file;
    const seq = ++this.sharpSeq;
    this.sharpTask?.cancel();
    this.sharpTask = null;
    if (this.destroyed || !img || !pane || !host || !file?.original_url || this.zoom <= 1 || !this.pageLoaded) {
      this.clearSharp();
      return;
    }
    const box = img.getBoundingClientRect();
    const view = pane.getBoundingClientRect();
    const region = sharpRegion(box, { left: view.left, top: view.top, width: pane.clientWidth, height: pane.clientHeight }, window.devicePixelRatio || 1);
    if (!region) {
      this.clearSharp();
      return;
    }
    try {
      this.pdfDoc ??= this.pdf.openDocument(file.original_url, PDF_PAGE_RANGES);
      const doc = await this.pdfDoc.promise;
      const page = await doc.getPage(file.page_number ?? this.data.revision.page_number);
      if (seq !== this.sharpSeq) {
        return;
      }
      const scale = (box.width / page.getViewport({ scale: 1 }).width) * region.pixelRatio;
      const r = region.pixelRatio;
      const render = this.pdf.startRenderRegion(page, scale, { x: region.x * r, y: region.y * r, width: region.width * r, height: region.height * r });
      this.sharpTask = render.task;
      const canvas = await render.done;
      if (seq !== this.sharpSeq || this.destroyed) {
        return;
      }
      // Inline: the canvas is made here, outside the component's encapsulated styles
      Object.assign(canvas.style, {
        position: 'absolute', display: 'block', background: '#ffffff',
        left: `${region.x}px`, top: `${region.y}px`, width: `${region.width}px`, height: `${region.height}px`,
      });
      canvas.setAttribute('data-testid', 'drawing-place-sharp');
      host.replaceChildren(canvas);
    } catch {
      // Cancelled by a newer zoom or scroll, or the PDF could not be read: the preview image stays
    }
  }

  private clearSharp(): void {
    this.sharpTask?.cancel();
    this.sharpTask = null;
    this.sharpHost?.nativeElement.replaceChildren();
  }

  /** Points mode: a click on the page starts a pair, or moves the sheet point still waiting for its map point. */
  onSheetClick(event: MouseEvent): void {
    // The click that ends a pan places nothing
    if (!this.sheetZoom.takeClick() || this.mode === 'region' || this.phase !== 'points') {
      return;
    }
    const point = sheetPointFromClick(event);
    if (point) {
      this.pendingSheet = point;
    }
  }

  /** Keyboard: the arrows move the waiting sheet point 1% of the page (starting at the centre). */
  onSheetKey(event: KeyboardEvent): void {
    const steps: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const step = steps[event.key];
    if (!step || this.mode === 'region' || this.phase !== 'points' || !this.pageLoaded) {
      return;
    }
    event.preventDefault();
    const c = (v: number) => Math.min(1, Math.max(0, v));
    const from = this.pendingSheet ?? { x: 0.5, y: 0.5 };
    this.pendingSheet = { x: c(from.x + step[0] / 100), y: c(from.y + step[1] / 100) };
  }

  onSheetMouseDown(event: MouseEvent): void {
    if (this.mode !== 'region' || this.phase !== 'points' || event.button !== 0) {
      return;
    }
    const point = this.pointAt(event);
    if (!point) {
      return;
    }
    event.preventDefault();
    this.dragFrom = point;
    this.region = { x0: point.x, y0: point.y, x1: point.x, y1: point.y };
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.dragFrom) {
      return;
    }
    const point = this.pointAt(event, true);
    if (point) {
      this.region = this.box(this.dragFrom, point);
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(event: MouseEvent): void {
    if (!this.dragFrom) {
      return;
    }
    const point = this.pointAt(event, true);
    const region = point ? this.box(this.dragFrom, point) : this.region;
    this.dragFrom = null;
    // A click without a drag is not an area; keep the whole page.
    this.region = region && region.x1 - region.x0 > 0.01 && region.y1 - region.y0 > 0.01 ? region : null;
  }

  toggleCheck(): void {
    this.nextIsCheck = !this.nextIsCheck;
  }

  removePair(index: number): void {
    if (this.method === 'stations') {
      this.stationPairs = this.stationPairs.filter((_, i) => i !== index);
    } else {
      this.pairs = this.pairs.filter((_, i) => i !== index);
    }
    this.snapped = false;
    this.refit();
    this.drawMapPoints();
  }

  cancelPending(): void {
    this.pendingSheet = null;
  }

  async onViewLoaded(view: __esri.MapView): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.mapView = view;
    this.alignmentLayer = new GraphicsLayer({ listMode: 'hide' });
    this.pointsLayer = new GraphicsLayer({ listMode: 'hide' });
    this.snapLayer = new GraphicsLayer({ listMode: 'hide' });
    view.map.add(this.alignmentLayer);
    view.map.add(this.pointsLayer);
    view.map.add(this.snapLayer);
    // The ticks and labels are sized in screen pixels: drawn again once a zoom settles
    this.handles.push(reactiveUtils.when(() => view.stationary, () => this.drawAlignment()));
    this.drawAlignment();
    if (this.method === 'stations') {
      this.fitAlignment();
    }
    this.applySavedBasemap();
    this.handles.push(view.on('click', event => void this.onMapClickEvent(view, event)));
    this.handles.push(view.on('pointer-move', event => void this.onMapPointerMove(view, event)));
    this.handles.push(view.on('pointer-leave', () => {
      this.clearSnap();
      this.setReadout(null);
    }));
    try {
      await this.referenceLayers.activateForProject(view, this.data.projectId, this.auth.getCurrentUser()?.id, () => this.auth.getCurrentUser()?.id);
      if (this.destroyed) {
        return;
      }
      this.syncLayerRows();
      await this.showVisibleLayers();
    } catch {
      this.layersToolbar = { ...this.layersToolbar, overlayLoading: false, overlayError: 'Design layers could not be loaded.' };
    }
    this.cdr.detectChanges();
  }

  async onLayerToggle(layerId: string): Promise<void> {
    const entry = this.referenceLayers.getActiveLayers().find(item => item.summary.id === layerId);
    if (!entry?.published) {
      return;
    }
    const visible = !this.referenceLayers.layerState(entry.published).visible;
    this.referenceLayers.savePublishedViewState(layerId, { visible }).catch(() => undefined);
    this.syncLayerRows();
    await this.showVisibleLayers();
  }

  onLayerOpacity(change: { id: string; opacity: number }): void {
    if (!Number.isFinite(change.opacity) || change.opacity < 0 || change.opacity > 1) {
      return;
    }
    this.referenceLayers.setOpacity(change.id, change.opacity);
    this.referenceLayers.savePublishedViewState(change.id, { opacity: change.opacity }).catch(() => undefined);
    this.syncLayerRows();
  }

  zoomToLayer(layerId: string): void {
    const view = this.mapView;
    const entry = this.referenceLayers.getActiveLayers().find(item => item.summary.id === layerId);
    const saved = entry?.summary.extent;
    if (!view || !saved || ![saved.xmin, saved.ymin, saved.xmax, saved.ymax].every(Number.isFinite)) {
      return;
    }
    const target = new Extent({ xmin: saved.xmin, ymin: saved.ymin, xmax: saved.xmax, ymax: saved.ymax, spatialReference: { wkid: 4326 } });
    view.goTo(target).then(() => {
      const zoom = clampZoomToPublishedMax(view.zoom, entry.published);
      return zoom === null ? undefined : view.goTo({ center: target.center, zoom });
    }).catch(() => undefined);
  }

  zoomMapBy(delta: number): void {
    this.mapView?.goTo({ zoom: this.mapView.zoom + delta }, { duration: 300 }).catch(() => undefined);
  }

  onBasemapChanged(basemap: { id?: string }): void {
    if (!basemap?.id) {
      return;
    }
    this.esriMap?.selectBasemap(basemap);
    this.basemapId = basemap.id;
    saveBasemapPreference(this.auth.getCurrentUser()?.id, this.data.projectId, basemap.id);
  }

  /** Control points and an optional check point, fitted by the converter in the project's UTM zone (R20). */
  place(): void {
    if (!this.canPlace) {
      return;
    }
    this.trackedMethod = this.method;
    if (this.method === 'stations') {
      void this.submit(stationPlaceBody(this.stationPairs, this.alignment));
      return;
    }
    const toPoint = (p: PlacePair) => ({ sheet: { x: p.sheet.x, y: p.sheet.y }, map: { lat: p.map.lat, lon: p.map.lon } });
    void this.submit({
      control_points: this.pairs.filter(p => !p.check).map(toPoint),
      check_points: this.pairs.filter(p => p.check).map(toPoint),
    });
  }

  /** W4: the API exposes no georeferenced flag, so this is always offered; a plain page fails with TOO_FEW_POINTS. */
  useEmbedded(): void {
    if (!this.canUseEmbedded) {
      return;
    }
    this.trackedMethod = 'embedded';
    void this.submit({ control_points: [], check_points: [] });
  }

  backToPoints(): void {
    this.phase = 'points';
    this.status = '';
  }

  private async submit(points: Pick<DrawingPlaceRequest, 'control_points' | 'check_points' | 'station_points' | 'alignment'>): Promise<void> {
    const body: DrawingPlaceRequest = { ...points, tolerance_m: TOLERANCE_M };
    if (this.region) {
      body.region = this.region;
    }
    if (this.supersedesLayerId) {
      body.supersedesLayerId = this.supersedesLayerId;
    }
    this.placing = true;
    this.error = '';
    try {
      const result = await firstValueFrom(this.api.place(this.data.projectId, this.data.revision.uuid, body));
      if (this.destroyed) {
        return;
      }
      this.phase = 'placing';
      this.status = `Placing ${this.title} on the map. This can take a minute.`;
      this.polls = 0;
      this.poll({ sourceUuid: result.sourceUuid, versionUuid: result.versionUuid, groupId: result.groupId, groupName: result.groupName });
    } catch (e) {
      this.track.track('placement_failed', { method: this.trackedMethod, ...errorProps(e) });
      const code = (e as { error?: { code?: string } })?.error?.code;
      if (code === 'PLACEMENT_LAYER_AMBIGUOUS' && this.data.flaggedLayers?.length) {
        this.chooseLayer = true;
        this.supersedesLayerId = null;
      } else if (code === 'PLACEMENT_LAYER_NOT_FLAGGED') {
        // The layer was re-placed or removed meanwhile: the next Place goes without it instead of failing again.
        this.chooseLayer = false;
        this.supersedesLayerId = null;
      }
      this.error = this.api.failureMessage(code, this.api.errorMessage(e));
    } finally {
      this.placing = false;
      this.cdr.detectChanges();
    }
  }

  /** Waits for the map import to finish; a failed import reports the converter's code. */
  private poll(placed: DrawingPlaceDialogResult): void {
    this.sources.getVersion(this.data.projectId, placed.versionUuid).subscribe({
      next: version => {
        if (this.destroyed) {
          return;
        }
        if (version.status === 'ready_for_review' || version.status === 'published') {
          const rms = this.trackedMethod === 'embedded' ? null : this.fit?.rms_m;
          this.track.track('placement_saved', {
            revision_uuid: this.data.revision.uuid, method: this.trackedMethod,
            rms_ft: typeof rms === 'number' ? Math.round(rms * 3.28084 * 10) / 10 : null,
            check_point: this.activePairs.some(p => p.check),
          });
          this.dialogRef.close(placed);
          return;
        }
        if (version.status === 'failed' || version.status === 'cancelled') {
          const code = version.job?.failureCode;
          this.track.track('placement_failed', { method: this.trackedMethod, error_code: code || 'IMPORT_FAILED', status: null });
          this.phase = 'failed';
          this.status = code?.startsWith('PLACEMENT_') ? this.api.failureMessage(code) : this.sources.failureMessage(code, 'pdf');
          this.cdr.detectChanges();
          return;
        }
        this.schedulePoll(placed);
      },
      error: () => this.schedulePoll(placed),
    });
  }

  private schedulePoll(placed: DrawingPlaceDialogResult): void {
    if (this.destroyed) {
      return;
    }
    if (++this.polls > POLL_LIMIT) {
      this.phase = 'failed';
      this.status = 'Placing is taking longer than usual. Check Settings › Map layers › Pending later.';
      this.cdr.detectChanges();
      return;
    }
    this.pollTimer = setTimeout(() => this.poll(placed), POLL_MS);
  }

  /** A map click: snapped to the nearest visible design-layer vertex within 12 px, unless Alt is held */
  private async onMapClickEvent(view: __esri.MapView, event: __esri.ViewClickEvent): Promise<void> {
    this.clearSnap();
    // By stations: the click picks the station (and offset / side) of the waiting sheet point on the alignment
    if (this.method === 'stations') {
      this.pickStationAt(view, event);
      return;
    }
    const raw = view.toMap(event);
    const snap = event.native?.altKey || !this.pendingSheet ? null : await this.findSnap(view, { x: event.x, y: event.y });
    // Only a click that made a pair says whether it snapped
    if (this.onMapClick(snap ?? raw)) {
      this.snapped = !!snap;
    }
  }

  /** While a map point is wanted: the snap marker follows the vertex a click would snap to */
  private async onMapPointerMove(view: __esri.MapView, event: __esri.ViewPointerMoveEvent): Promise<void> {
    if (this.method === 'stations') {
      this.readoutAt(view, event);
      return;
    }
    if (this.phase !== 'points' || this.method !== 'points' || !this.pendingSheet || event.native?.altKey) {
      this.clearSnap();
      return;
    }
    const at = { x: event.x, y: event.y };
    const wait = SNAP_HOVER_MS - (Date.now() - this.snapHoverAt);
    if (this.snapHoverTimer) {
      clearTimeout(this.snapHoverTimer);
      this.snapHoverTimer = null;
    }
    if (wait > 0) {
      this.snapHoverTimer = setTimeout(() => {
        this.snapHoverTimer = null;
        void this.hoverSnap(view, at);
      }, wait);
      return;
    }
    await this.hoverSnap(view, at);
  }

  /** Latest wins: while a query runs, a newer position waits and runs next; older ones are dropped */
  private async hoverSnap(view: __esri.MapView, at: { x: number; y: number }): Promise<void> {
    if (this.destroyed || this.phase !== 'points' || !this.pendingSheet) {
      return;
    }
    if (this.snapHoverBusy) {
      this.snapHoverNext = at;
      return;
    }
    this.snapHoverBusy = true;
    this.snapHoverAt = Date.now();
    const sequence = ++this.snapSequence;
    try {
      const snap = await this.findSnap(view, at);
      // A clearSnap (Alt, leave, click, close) since this started bumped the sequence: show nothing
      if (sequence === this.snapSequence) {
        this.showSnapMarker(snap);
      }
    } finally {
      this.snapHoverBusy = false;
      const next = this.snapHoverNext;
      this.snapHoverNext = null;
      if (next && sequence === this.snapSequence) {
        void this.hoverSnap(view, next);
      }
    }
  }

  /** Drops the marker and any hover work pending or in flight, so nothing repaints it afterwards */
  private clearSnap(): void {
    this.snapSequence++;
    this.snapHoverNext = null;
    if (this.snapHoverTimer) {
      clearTimeout(this.snapHoverTimer);
      this.snapHoverTimer = null;
    }
    this.showSnapMarker(null);
  }

  /**
   * The vertex to snap to: the features the map already holds for the visible design layers (GeoJSON and feature
   * layers; layers drawn only as image tiles have no client features and do not snap), queried client-side around the
   * pointer by their layer views (no request), turned into screen points and the nearest picked.
   */
  private async findSnap(view: __esri.MapView, screen: { x: number; y: number }): Promise<__esri.Point | null> {
    const centre = view.toMap(screen);
    if (!centre) {
      return null;
    }
    const pad = SNAP_RADIUS_PX * view.resolution;
    const box = { xmin: centre.x - pad, ymin: centre.y - pad, xmax: centre.x + pad, ymax: centre.y + pad };
    const area = new Extent({ ...box, spatialReference: view.spatialReference });
    // allLayerViews: a group layer's children have their views there, not in layerViews. A scale-suspended view may
    // hold generalised geometry, so it does not snap.
    const views = this.snapLayers()
      .map(layer => view.allLayerViews.find(lv => lv.layer === layer) as __esri.FeatureLayerView | undefined)
      .filter((lv): lv is __esri.FeatureLayerView => !!lv?.queryFeatures && !lv.suspended);
    const results = await Promise.all(views.map(lv =>
      lv.queryFeatures({ geometry: area, spatialRelationship: 'intersects', returnGeometry: true, outSpatialReference: view.spatialReference })
        // A layer view still updating: no snap from it this time
        .catch(() => null)));
    const candidates: ScreenVertex<__esri.Point>[] = [];
    for (const result of results) {
      for (const feature of result?.features ?? []) {
        // Only vertices inside the pad (map units) become points: a long line's thousands of far vertices cost nothing
        for (const [x, y] of geometryVertices(feature.geometry?.toJSON?.() ?? feature.geometry, undefined, box)) {
          const point = new Point({ x, y, spatialReference: view.spatialReference });
          const at = view.toScreen(point);
          if (at) {
            candidates.push({ x: at.x, y: at.y, ref: point });
          }
        }
      }
    }
    return nearestVertex(screen, candidates)?.ref ?? null;
  }

  /** Visible design layers that hold features in the browser: GeoJSON and feature layers, inside group layers too */
  private snapLayers(): __esri.Layer[] {
    const out: __esri.Layer[] = [];
    const walk = (layer: __esri.Layer) => {
      if (!layer?.visible) {
        return;
      }
      const children = (layer as __esri.GroupLayer).layers;
      if (children?.length) {
        children.forEach(child => walk(child));
      } else if (layer.type === 'geojson' || layer.type === 'feature') {
        out.push(layer);
      }
    };
    this.referenceLayers.getActiveLayers().forEach(entry => walk(entry.layer));
    return out;
  }

  private showSnapMarker(point: __esri.Point | null): void {
    const layer = this.snapLayer;
    if (!layer) {
      return;
    }
    layer.removeAll();
    if (point) {
      layer.add(new Graphic({
        geometry: point,
        symbol: new SimpleMarkerSymbol({ style: 'square', color: [0, 0, 0, 0], size: 14, angle: 45, outline: { color: CHECK_COLOR, width: 2 } }),
      }));
    }
  }

  /** Adds the pair for the waiting sheet point; false when the click made none */
  private onMapClick(point: __esri.Point | null): boolean {
    if (this.phase !== 'points' || !point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) {
      return false;
    }
    if (!this.pendingSheet) {
      this.error = 'Click a spot on the sheet first.';
      this.cdr.detectChanges();
      return false;
    }
    this.error = '';
    // A view panned across the antimeridian reports longitudes past ±180.
    const lon = ((((point.longitude + 180) % 360) + 360) % 360) - 180;
    this.pairs = [...this.pairs, { sheet: this.pendingSheet, map: { lat: point.latitude, lon }, check: this.nextIsCheck }];
    this.pendingSheet = null;
    this.clearSnap();
    this.resetNextPoint();
    this.refit();
    this.drawMapPoints();
    this.cdr.detectChanges();
    return true;
  }

  private refit(): void {
    const img = this.pageImage?.nativeElement;
    const page = img && sheetImageReady(img) ? { width: img.naturalWidth, height: img.naturalHeight } : null;
    this.fit = page ? fitSimilarity(this.previewPairs(), page, this.method === 'stations' ? 2 : 3) : null;
  }

  /** The current method's points with ground positions: station points through the alignment */
  private previewPairs(): PlacePair[] {
    if (this.method === 'points') {
      return this.pairs;
    }
    return this.stationPairs.filter(p => !!p.map).map(p => ({ sheet: p.sheet, map: p.map, check: p.check }));
  }

  private stationPoint(station: number, offsetFt: number, side: 'L' | 'R' | null): { lat: number; lon: number } | null {
    return this.alignment ? stationGround(this.alignment, station, offsetFt, side) : null;
  }

  /**
   * Clears the next point's Check toggle and station form. With coalesced change detection the event that ticked or
   * typed them may not have rendered yet, and resetting an unrendered value is no change to the bindings, so it would
   * stay on screen: render first.
   */
  private resetNextPoint(): void {
    this.cdr.detectChanges();
    this.nextIsCheck = false;
    this.stationText = '';
    this.offsetText = '0';
    this.sideChoice = 'on';
  }

  private drawMapPoints(): void {
    const layer = this.pointsLayer;
    if (!layer) {
      return;
    }
    layer.removeAll();
    const shown: { map: { lat: number; lon: number } | null; check?: boolean }[] = this.method === 'stations' ? this.stationPairs : this.pairs;
    shown.forEach((p, i) => {
      if (!p.map) {
        return;
      }
      const geometry = new Point({ latitude: p.map.lat, longitude: p.map.lon });
      const color = p.check ? CHECK_COLOR : CONTROL_COLOR;
      layer.add(new Graphic({ geometry, symbol: new SimpleMarkerSymbol({ style: 'circle', color, size: 18, outline: { color: '#FFFFFF', width: 1.5 } }) }));
      layer.add(new Graphic({
        geometry,
        symbol: new TextSymbol({ text: this.label(i), color: '#FFFFFF', yoffset: -4, font: { family: 'Montserrat', size: 9, weight: 'bold' } }),
      }));
    });
    // By stations: the waiting point where its station (picked or typed) puts it, numbered as it will be
    const draft = this.draftStationPoint();
    if (draft) {
      const geometry = new Point({ latitude: draft.lat, longitude: draft.lon });
      const color = this.nextIsCheck ? CHECK_COLOR : CONTROL_COLOR;
      layer.add(new Graphic({ geometry, symbol: new SimpleMarkerSymbol({ style: 'circle', color, size: 18, outline: { color: '#FFFFFF', width: 1.5 } }) }));
      layer.add(new Graphic({
        geometry,
        symbol: new TextSymbol({ text: this.label(shown.length), color: '#FFFFFF', yoffset: -4, font: { family: 'Montserrat', size: 9, weight: 'bold' } }),
      }));
    }
  }

  /** The waiting sheet point's ground point from the station form, when it reads */
  private draftStationPoint(): { lat: number; lon: number } | null {
    const alignment = this.alignment;
    if (this.method !== 'stations' || !this.pendingSheet || !alignment || !this.stationText.trim()) {
      return null;
    }
    const entry = readStationEntry(alignment, { station: this.stationText, offset: this.offsetText, side: entrySide(this.sideChoice) || 'R' });
    return entry.ok === true ? entry.map : null;
  }

  /** Ground metres per screen pixel at a latitude (the view's resolution is Web Mercator metres) */
  private groundMetresPerPx(view: __esri.MapView, lat: number): number {
    return view.resolution * Math.cos((lat * Math.PI) / 180);
  }

  /** By stations: a map click fills the station, offset and side of the waiting sheet point (Alt: the exact offset) */
  private pickStationAt(view: __esri.MapView, event: __esri.ViewClickEvent): void {
    const alignment = this.alignment;
    const point = view.toMap(event);
    if (this.phase !== 'points' || !alignment || !this.pendingSheet || point?.latitude === undefined) {
      return;
    }
    const at = { lat: point.latitude, lon: point.longitude };
    const snap = snapToAlignment(alignment, at, this.groundMetresPerPx(view, at.lat), !!event.native?.altKey);
    if (!snap) {
      this.error = `That point is beyond the ends of ${alignment.name || 'the alignment'} (${this.alignmentRangeText}).`;
      this.cdr.detectChanges();
      return;
    }
    this.error = '';
    this.stationText = stationInputText(snap.station);
    this.offsetText = String(snap.offset_ft);
    this.sideChoice = snap.onLine ? 'on' : snap.side;
    this.drawMapPoints();
    this.cdr.detectChanges();
  }

  /** By stations: "STA 1006+42 · 12 ft R" beside the pointer while it is near the line */
  private readoutAt(view: __esri.MapView, event: __esri.ViewPointerMoveEvent): void {
    const alignment = this.alignment;
    const point = alignment ? view.toMap({ x: event.x, y: event.y }) : null;
    if (!alignment || point?.latitude === undefined) {
      this.setReadout(null);
      return;
    }
    const at = { lat: point.latitude, lon: point.longitude };
    const mpp = this.groundMetresPerPx(view, at.lat);
    const snap = snapToAlignment(alignment, at, mpp, !!event.native?.altKey);
    const exact = snapToAlignment(alignment, at, mpp, true);
    if (!snap || !exact || (exact.offset_ft * FT) / mpp > READOUT_PX) {
      this.setReadout(null);
      return;
    }
    this.setReadout({ text: stationReadout(snap), x: event.x, y: event.y });
  }

  private setReadout(readout: { text: string; x: number; y: number } | null): void {
    if (!readout && !this.hoverReadout) {
      return;
    }
    this.hoverReadout = readout;
    this.cdr.detectChanges();
  }

  /**
   * By stations: the chosen alignment on the map, its line, and zoom-aware station marks (Sri): labels on the 1+00 / 5+00
   * / 10+00 / 50+00 / 100+00 ladder at least 60 px apart, ticks on a finer step at least 8 px apart (none when tighter),
   * the start and end always labelled. Drawn again once each zoom settles. Only while By stations is open.
   */
  private drawAlignment(): void {
    const layer = this.alignmentLayer;
    const view = this.mapView;
    if (!layer) {
      return;
    }
    layer.removeAll();
    const alignment = this.alignment;
    if (this.method !== 'stations' || !alignment || !view) {
      return;
    }
    const coords = alignment.alignment.coords;
    layer.add(new Graphic({
      geometry: new Polyline({ paths: [coords.map(([lon, lat]) => [lon, lat])], spatialReference: { wkid: 4326 } }),
      symbol: new SimpleLineSymbol({ color: ALIGNMENT_COLOR, width: 2 }),
    }));
    const lat = coords[Math.floor(coords.length / 2)]?.[1] ?? 0;
    const mpp = this.groundMetresPerPx(view, lat) || 1;
    const halfFt = (TICK_HALF_PX * mpp) / FT;
    const { labelEvery, tickEvery } = stationIntervals(mpp);
    const tick = (left: { lat: number; lon: number }, right: { lat: number; lon: number }, width: number) => layer.add(new Graphic({
      geometry: new Polyline({ paths: [[[left.lon, left.lat], [right.lon, right.lat]]], spatialReference: { wkid: 4326 } }),
      symbol: new SimpleLineSymbol({ color: ALIGNMENT_COLOR, width }),
    }));
    if (tickEvery) {
      for (const t of stationTicks(alignment, tickEvery, halfFt)) {
        tick(t.left, t.right, labelEvery && t.station % labelEvery === 0 ? 2 : 1);
      }
    }
    const { first, last } = stationRange(alignment.alignment);
    for (const station of stationLabelStations(first, last, labelEvery, mpp)) {
      const end = station === first || station === last;
      const left = stationGround(alignment, station, halfFt, 'L');
      const right = stationGround(alignment, station, halfFt, 'R');
      if (!left || !right) {
        continue;
      }
      // The ends get a tick of their own (they are seldom on the step)
      if (end) {
        tick(left, right, 2);
      }
      layer.add(new Graphic({
        geometry: new Point({ latitude: left.lat, longitude: left.lon }),
        symbol: new TextSymbol({
          text: stationName(station), color: '#031356', haloColor: '#FFFFFF', haloSize: 1.5, horizontalAlignment: 'left', xoffset: 4,
          font: { family: 'Montserrat', size: 9, weight: 'bold' },
        }),
      }));
    }
  }

  /** By stations opens on the sheet's stations along the alignment (clipped to it), else the whole alignment */
  private fitAlignment(): void {
    const alignment = this.alignment;
    const view = this.mapView;
    if (!alignment || !view) {
      return;
    }
    const range = stationRange(alignment.alignment);
    const s = this.data.stations;
    const sheet = s && Number.isFinite(s.start) && Number.isFinite(s.end) ? stationRangeBounds(alignment, s.start, s.end) : null;
    const box = sheet ?? stationRangeBounds(alignment, range.first, range.last);
    if (!box) {
      return;
    }
    const extent = new Extent({ ...box, spatialReference: { wkid: 4326 } });
    view.goTo(extent.expand(1.4)).catch(() => undefined);
  }

  /** The pointer over the page image in page space; `clamp` keeps a drag that leaves the image on its edge. */
  private pointAt(event: MouseEvent, clamp = false): { x: number; y: number } | null {
    const img = this.pageImage?.nativeElement;
    if (!sheetImageReady(img)) {
      return null;
    }
    const rect = img.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    if (!clamp && (x < 0 || x > 1 || y < 0 || y > 1)) {
      return null;
    }
    const c = (v: number) => Math.min(1, Math.max(0, v));
    return { x: c(x), y: c(y) };
  }

  private box(a: { x: number; y: number }, b: { x: number; y: number }) {
    return { x0: Math.min(a.x, b.x), y0: Math.min(a.y, b.y), x1: Math.max(a.x, b.x), y1: Math.max(a.y, b.y) };
  }

  private syncLayerRows(): void {
    const entries = this.referenceLayers.getActiveLayers().filter(entry => entry.published);
    this.layersToolbar = {
      ...this.layersToolbar,
      overlayLoading: false,
      visibleOverlayIds: entries.filter(entry => this.referenceLayers.layerState(entry.published).visible).map(entry => entry.summary.id),
      overlayLayers: entries.map(entry => ({
        id: entry.summary.id, name: entry.summary.name, opacity: entry.layer.opacity,
        group: this.referenceLayers.publishedGroupName(entry.published), extent: entry.summary.extent, status: entry.status, error: entry.error,
      })),
    };
  }

  private async showVisibleLayers(): Promise<void> {
    const view = this.mapView;
    if (!view) {
      return;
    }
    const visible = new Set(this.layersToolbar.visibleOverlayIds);
    await this.referenceLayers.showOnMap(view, entry => visible.has(entry.summary.id));
    if (!this.destroyed && this.pointsLayer) {
      view.map.reorder(this.pointsLayer, view.map.layers.length - 1);
    }
    if (!this.destroyed && this.snapLayer) {
      view.map.reorder(this.snapLayer, view.map.layers.length - 1);
    }
  }

  private applySavedBasemap(): void {
    const basemap = resolveBasemap(this.basemapId, this.organizationBasemaps);
    if (!basemap || basemap.id === 'default') {
      this.basemapId = 'default';
      return;
    }
    this.esriMap?.selectBasemap(basemap);
  }
}
